<template>
  <v-container class="pt-5 pb-7">
    <v-layout wrap>
      <v-flex v-if="!getWorkflowStatus" class="text-xs-center pt-5">
        <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
      </v-flex>

      <v-flex v-if="getWorkflowStatus === 'Under Construction'" md8 class="mb-5">
        <h3>Ready to submit your curriculum?</h3>
        <p>Once your materials have been uploaded and organized, click the "Ready for Review" button below to initiate the review process. If a peer reviewer has requested changes from you, you will be able to see those requests below with further details.</p>
        <div>
          <v-btn color="secondary" class="mx-0" :loading="processingReadyForReview" @click="readyForReview">
            Ready For Review
          </v-btn>
        </div>
      </v-flex>

      <v-flex v-if="getWorkflowStatus === 'Ready for Review'" md8 class="mb-5">
        <h3>Your curriculum is currently under review</h3>
        <p>Once your curriculum has been reviewed, you will be able to see any changes that were requested below (if any has been requested).</p>
      </v-flex>

      <v-flex v-if="getWorkflowStatus === 'Changes Requested'" md8 class="mb-5">
        <h3>Your curriculum requires further changes</h3>
        <p>Your curriculum has been reviewed and needs to have further changes made before submitting for publication. You can see those unresolved comments below. Once you have made the requested changes, you can resubmit your curriculum for review again.</p>
        <div>
          <v-btn color="secondary" class="mx-0" :loading="processingReadyForReview" @click="readyForReview">
            Resubmit For Review
          </v-btn>
        </div>
      </v-flex>

      <v-flex v-if="getWorkflowStatus === 'No Changes Requested' || getWorkflowStatus === 'Ready for Publication'" md12 class="mb-5">
        <h3 class="text-center mt-5">
          Your curriculum has finished the peer review process
        </h3>
      </v-flex>

      <v-flex v-if="getWorkflowStatus === 'No Changes Requested' || getWorkflowStatus === 'Ready for Publication'" xs12 class="mt-5">
        <v-form @submit.prevent="saveMetadata">
          <v-layout justify-center wrap>
            <v-flex md3 px-3>
              <v-menu v-model="reviewDateMenu" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="reviewDateFormatted" label="Next Scheduled Update" box @blur="reviewDate = parseDate(reviewDateFormatted)" v-on="on" />
                </template>
                <v-date-picker v-model="reviewDate" header-color="secondary" color="secondary" @input="reviewDateMenu = false" />
              </v-menu>
            </v-flex>
            <v-flex md3 pl-3>
              <v-select v-model="updateForm.reviewPeriod" :items="['Annual', 'Biennial']" box label="Update Period" />
            </v-flex>
          </v-layout>
          <v-layout justify-center wrap>
            <v-flex md6 class="text-right">
              <v-btn color="secondary" class="mx-0" type="submit" :loading="savingMetadata">
                Update
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-flex>
    </v-layout>

    <review-screen v-if="getWorkflowStatus === 'Changes Requested'" :course-id="courseId" />
  </v-container>
</template>
<script>
  import axios from 'axios';
  import moment from 'moment';
  import { mapActions } from 'vuex';
  import ManageCourseBase from '../ManageCourseBase.vue';
  import ReviewScreen from './ReviewScreen.vue';

  export default {
    components: {
      ReviewScreen,
    },

    extends: ManageCourseBase,

    data: vm => ({
      metadata: null,
      savingMetadata: false,
      processingReadyForReview: false,
      reviewDateFormatted: vm.formatDate(new Date().toISOString().substr(0, 10)),
      reviewDateMenu: false,
      peerReviewers: [],
      workflowStatuses: [],
      updateForm: {
        reviewPeriod: '',
        nextReviewDate: new Date().toISOString().substr(0, 10),
      },
    }),

    computed: {
      reviewDate: {
        get() {
          if (moment(this.updateForm.nextReviewDate).format('MM/DD/YYYY') === '01/01/0001' || !this.updateForm.nextReviewDate) {
            return new Date().toISOString().substr(0, 10);
          }
          const [month, day, year] = moment(this.updateForm.nextReviewDate).format('MM/DD/YYYY').split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        },
        set(value) {
          this.updateForm.nextReviewDate = value;
        },
      },

      getWorkflowStatus() {
        if (this.metadata) {
          return this.metadata.workflowStatus ? this.metadata.workflowStatus.name : null;
        }

        return null;
      },
    },

    watch: {
      'updateForm.nextReviewDate': function (val) {
        this.reviewDateFormatted = this.formatDate(val);
      },
    },

    mounted() {
      this.getMetadata();
    },

    methods: {
      ...mapActions({
        setToast: 'toasts/addToGlobalToastQueue',
      }),

      async getMetadata() {
        const { data } = await axios.get(`/api/courses/${this.courseId}`);
        this.metadata = data;
        this.updateForm = JSON.parse(JSON.stringify(data));
      },

      async saveMetadata() {
        this.savingMetadata = true;
        try {
          await axios.patch(`/api/courses/${this.courseId}`, this.updateForm);
          this.savingMetadata = false;
          this.setToast({
            color: 'success',
            message: 'Course info has been saved',
            multiline: false,
          });
        } catch (error) {
          this.setToast({
            color: 'error',
            message: error.response.data[0],
            multiline: true,
          });
          this.savingMetadata = false;
        }
      },

      async readyForReview() {
        this.processingReadyForReview = true;
        try {
          await axios.post(`/api/courses/${this.courseId}/workflow/start-review`);
          await this.getMetadata();
          this.setToast({
            color: 'success',
            message: 'Your course has been submitted for review! You will be notified when your course has finalized the review process.',
            duration: 5000,
            multiline: true,
          });
        } catch (error) {
          this.setToast({
            color: 'error',
            message: error.response.data[0],
            multiline: true,
          });
        } finally {
          this.processingReadyForReview = false;
        }
      },
    },
  };
</script>